/**
 * Autoplay betting strategies as pure data.
 *
 * Each strategy is a pair of step functions: one applied after a winning round,
 * one after a losing round. Each step takes the current strategy context (base
 * bet, current bet, internal counters, manual percentage knobs) and returns
 * the next bet plus updated counters. The store consumes this; React doesn't
 * touch it.
 *
 * Adding a new strategy = add an entry to STRATEGIES. No branches in the
 * call site.
 *
 * Strategies modelled after Stake / BC.Game / Roobet UI conventions.
 */

export type Strategy =
  | "manual"
  | "martingale"
  | "paroli"
  | "fibonacci"
  | "dalembert";

export interface StrategyContext {
  /** The user's chosen anchor bet. Strategies reset to this on the appropriate event. */
  baseBet: number;
  /** What was actually staked on the round just resolved. */
  currentBet: number;
  /** Fibonacci-internal cursor (count of consecutive losses since last reset). */
  fibIndex: number;
  /** D'Alembert-internal counter (units above the floor). */
  unitCount: number;
  /** Manual mode: % delta to apply on win (e.g., 0 = reset, 100 = double). */
  onWinPct: number;
  /** Manual mode: % delta on loss. */
  onLossPct: number;
}

export interface StrategyResult {
  nextBet: number;
  fibIndex: number;
  unitCount: number;
}

type Step = (ctx: StrategyContext) => StrategyResult;

function fib(n: number): number {
  if (n <= 1) return 1;
  let a = 1;
  let b = 1;
  for (let i = 2; i <= n; i++) {
    const t = a + b;
    a = b;
    b = t;
  }
  return b;
}

const reset = (ctx: StrategyContext): StrategyResult => ({
  nextBet: ctx.baseBet,
  fibIndex: 0,
  unitCount: 0,
});

export const STRATEGIES: Record<Strategy, { onWin: Step; onLoss: Step }> = {
  manual: {
    onWin: (ctx) => ({
      nextBet: Math.max(0, ctx.currentBet * (1 + ctx.onWinPct / 100)),
      fibIndex: ctx.fibIndex,
      unitCount: ctx.unitCount,
    }),
    onLoss: (ctx) => ({
      nextBet: Math.max(0, ctx.currentBet * (1 + ctx.onLossPct / 100)),
      fibIndex: ctx.fibIndex,
      unitCount: ctx.unitCount,
    }),
  },
  martingale: {
    onWin: reset,
    onLoss: (ctx) => ({
      nextBet: ctx.currentBet * 2,
      fibIndex: ctx.fibIndex,
      unitCount: ctx.unitCount,
    }),
  },
  paroli: {
    onWin: (ctx) => ({
      nextBet: ctx.currentBet * 2,
      fibIndex: ctx.fibIndex,
      unitCount: ctx.unitCount,
    }),
    onLoss: reset,
  },
  fibonacci: {
    onWin: reset,
    onLoss: (ctx) => {
      const nextIdx = ctx.fibIndex + 1;
      return {
        nextBet: ctx.baseBet * fib(nextIdx),
        fibIndex: nextIdx,
        unitCount: ctx.unitCount,
      };
    },
  },
  dalembert: {
    onWin: (ctx) => {
      const nextUnits = Math.max(0, ctx.unitCount - 1);
      return {
        nextBet: ctx.baseBet * Math.max(1, nextUnits + 1),
        fibIndex: ctx.fibIndex,
        unitCount: nextUnits,
      };
    },
    onLoss: (ctx) => {
      const nextUnits = ctx.unitCount + 1;
      return {
        nextBet: ctx.baseBet * (nextUnits + 1),
        fibIndex: ctx.fibIndex,
        unitCount: nextUnits,
      };
    },
  },
};

export const STRATEGY_LABELS: Record<Strategy, string> = {
  manual: "Manual",
  martingale: "Martingale",
  paroli: "Paroli (anti-Martingale)",
  fibonacci: "Fibonacci",
  dalembert: "D'Alembert",
};

export const STRATEGY_BLURBS: Record<Strategy, string> = {
  manual: "Use the % fields to control how the bet adjusts after each result.",
  martingale: "Double the bet after every loss; reset to base on a win.",
  paroli: "Double the bet after every win; reset to base on a loss.",
  fibonacci: "Step through the Fibonacci sequence on losses; reset on a win.",
  dalembert: "Add one base unit on a loss; subtract one on a win.",
};

/**
 * Apply a strategy step. Pure; returns the next state without mutating
 * inputs. The store passes the result straight into the autoplay runtime.
 */
export function applyStrategy(
  strategy: Strategy,
  outcome: "win" | "bust",
  ctx: StrategyContext,
): StrategyResult {
  const step = outcome === "win" ? STRATEGIES[strategy].onWin : STRATEGIES[strategy].onLoss;
  return step(ctx);
}
